import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { GitCompare, Building2, HeartPulse } from "lucide-react";

/** Iter 174 · Public brand-comparison share view. Read-only, no auth. */

const HEALTH_TONE = {
  excellent: "text-emerald-700 bg-emerald-50 border-emerald-300",
  good: "text-sky-700 bg-sky-50 border-sky-300",
  needs_work: "text-amber-700 bg-amber-50 border-amber-300",
  critical: "text-rose-700 bg-rose-50 border-rose-300",
};

function Row({ label, values, isList, isText, isMulti }) {
  return (
    <tr className="border-t border-border align-top">
      <th className="py-2 px-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-40 bg-muted/30 text-left">
        {label}
      </th>
      {values.map((v, i) => (
        <td key={i} className="py-2 px-3 text-xs align-top">
          {isList ? (
            !Array.isArray(v) || v.length === 0
              ? <span className="text-muted-foreground">—</span>
              : <ul className="space-y-0.5">{v.slice(0, 8).map((x, j) =>
                  <li key={j}>• {typeof x === "string" ? x : (x.title || x.name || JSON.stringify(x))}</li>)}</ul>
          ) : isMulti ? (
            !v || Object.keys(v || {}).length === 0
              ? <span className="text-muted-foreground">—</span>
              : Object.entries(v).map(([k, vv]) => (
                <div key={k}>
                  <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{k}</span>
                  <div>{Array.isArray(vv) ? vv.slice(0, 6).map((x, i2) =>
                    <div key={i2}>• {typeof x === "string" ? x : (x.title || x.name || JSON.stringify(x))}</div>) : (vv || "—")}</div>
                </div>))
          ) : isText ? (
            <div className="whitespace-pre-wrap max-w-[260px]">{v || <span className="text-muted-foreground">—</span>}</div>
          ) : (
            v || <span className="text-muted-foreground">—</span>
          )}
        </td>
      ))}
    </tr>
  );
}

export default function BrandCompareShare() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    api.get(`/brand-compare/share/${token}`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => setState({ loading: false,
        error: e?.response?.data?.detail || "Share link invalid or revoked",
        data: null }));
  }, [token]);

  if (state.loading) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }
  if (state.error) {
    return (
      <div className="p-12 text-center max-w-md mx-auto" data-testid="compare-share-error">
        <h1 className="text-2xl font-bold mb-2">Link unavailable</h1>
        <p className="text-sm text-muted-foreground">{state.error}</p>
      </div>
    );
  }

  const { snapshot, workspace, data } = state.data;
  const brands = data?.brands || [];

  return (
    <div className="min-h-screen bg-muted/20" data-testid="brand-compare-share-view">
      <header className="bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {workspace?.logo_url && <img src={workspace.logo_url} alt="" className="w-8 h-8 object-contain" />}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {workspace?.name || "Acquilix"} · Shared comparison
              </div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <GitCompare className="w-5 h-5" /> {snapshot.name}
              </h1>
            </div>
          </div>
          <div className="ml-auto text-[10px] text-right text-muted-foreground">
            Shared by {snapshot.created_by_name || "—"}<br />
            {new Date(snapshot.created_at).toLocaleString()}
          </div>
        </div>
        {snapshot.notes && (
          <div className="max-w-6xl mx-auto px-6 pb-4 text-sm whitespace-pre-wrap text-foreground/80">
            {snapshot.notes}
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 overflow-x-auto">
        <table className="min-w-full text-sm bg-background border border-border">
          <thead className="border-b border-border">
            <tr>
              <th className="py-3 px-3 text-left w-40 bg-muted/40"></th>
              {brands.map((b) => (
                <th key={b.id} className="py-3 px-3 text-left align-top border-l border-border min-w-[240px]">
                  <div className="flex items-center gap-2">
                    {b.logo_url
                      ? <img src={b.logo_url} alt="" className="w-8 h-8 object-contain border border-border bg-white" />
                      : <div className="w-8 h-8 border border-border bg-muted/40 flex items-center justify-center"><Building2 className="w-4 h-4 text-muted-foreground" /></div>}
                    <div>
                      <div className="font-semibold">{b.name}</div>
                      <span className={`text-[9px] uppercase tracking-wider border px-1 py-0.5 ${HEALTH_TONE[b.health_band] || ""}`}>
                        <HeartPulse className="w-2.5 h-2.5 inline mr-0.5" />
                        {b.health_score}
                      </span>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label="Client" values={brands.map((b) => b.client_name)} />
            <Row label="Industry" values={brands.map((b) => [b.industry, b.sub_industry].filter(Boolean).join(" · "))} />
            <Row label="Website" values={brands.map((b) => b.website
              ? <a key={b.id} href={b.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{b.website.replace(/^https?:\/\//, "").slice(0, 40)}</a>
              : "—")} />
            <Row label="Tagline" isText values={brands.map((b) => (b.core || {}).tagline)} />
            <Row label="Mission" isText values={brands.map((b) => (b.core || {}).mission)} />
            <Row label="Vision" isText values={brands.map((b) => (b.core || {}).vision)} />
            <Row label="Positioning" isText values={brands.map((b) => (b.core || {}).positioning)} />
            <Row label="Values" isList values={brands.map((b) => (b.core || {}).values || [])} />
            <Row label="USPs" isList values={brands.map((b) => b.usps || [])} />
            <Row label="SWOT" isMulti values={brands.map((b) => b.swot || {})} />
            <Row label="Personas" isList values={brands.map((b) => (b.strategy || {}).personas || [])} />
            <Row label="Voice attributes" isList values={brands.map((b) => (b.voice || {}).attributes || [])} />
            <Row label="Pillars" isList values={brands.map((b) => (b.pillars || []).map((p) => `${p.name || ""} (${p.target_pct || 0}%)`))} />
            <Row label="Competitors" isList values={brands.map((b) => b.competitors || [])} />
          </tbody>
        </table>
        <div className="mt-6 text-center text-[10px] text-muted-foreground">
          Generated by Acquilix — read-only share view.
        </div>
      </main>
    </div>
  );
}
